import { customizer } from '@consumer/api'
import { getForegroundColor } from '@consumer/helpers/color'
import { titleCase } from '@consumer/helpers/string'
import { v3WithImageBg } from '@consumer/logic/v3'
import type GiftTemplate from '@consumer/types/GiftTemplate'
export * from '@consumer/logic/v3'

// Public: Returns the card text in v3 giftlys.
export function getTagline (gift: GiftTemplate) {
  return gift.giftlyMetadata?.tagline || gift.tagline
}

// Public: Come up with some default recommended phrases to use in the card text in v3 giftlys.
export function generateDefaultTaglines (gift: GiftTemplate, allowOnlyPlaceName: boolean = true) {
  const taglines = []

  let defaultTagline = ''

  const place = gift.places?.[0]

  if (!place?.name) {
    defaultTagline = 'Any Place'
  }
  else {
    if (!place.placeType || place.placeType === 'general') {
      defaultTagline = titleCase(place.name, true)
    }
    else {
      if (allowOnlyPlaceName) {
        defaultTagline = place.name
      }
      else {
        defaultTagline = `Anything at ${place.name}`
      }
    }
  }

  if ((gift.places?.length ?? 0) > 1) {
    taglines.push(`${defaultTagline} and ${gift.places!.length - 1} other place${gift.places!.length > 2 ? 's' : ''}`)
    taglines.push(`${defaultTagline} and ${gift.places!.length - 1} more`)
  }
  else {
    taglines.push(defaultTagline)
  }

  return taglines.concat([
    'Treat Yo Self',
    'A Special Gift, Just For You',
    'Enjoy A Special Treat',
  ])
}

// Public: Comes up with a short phrase to use in the card text in v3 giftlys.
export function generateDefaultTagline (gift: GiftTemplate, allowOnlyPlaceName: boolean = true) {
  return generateDefaultTaglines(gift, allowOnlyPlaceName)[0]
}

// Public: Sets the card text used in v3 giftlys.
export function setTagline (
  gift: GiftTemplate,
  tagline?: string,
  allowEmptyTagline: boolean = false,
  allowOnlyPlaceName: boolean = true,
) {
  gift.giftlyMetadata ||= {}
  if (tagline || allowEmptyTagline) {
    gift.giftlyMetadata.tagline = tagline
    gift.giftlyMetadata.isCustomTagline = true
  }
  else {
    gift.giftlyMetadata.tagline = generateDefaultTagline(gift, allowOnlyPlaceName)
    gift.giftlyMetadata.isCustomTagline = false
  }
}

export function setTaglineUnlessCustom (gift: GiftTemplate, allowOnlyPlaceName: boolean = true) {
  if (!gift.giftlyMetadata?.isCustomTagline || (gift.places?.length ?? 0) <= 1) {
    setTagline(gift, undefined, false, allowOnlyPlaceName)
  }
}

export function isCurrentMaskColor (gift: GiftTemplate, color: string) {
  return color === gift.giftlyMetadata?.maskColor
}

export function setMaskColor (gift: GiftTemplate, color: string) {
  const meta = gift.giftlyMetadata ||= {}
  meta.designName = v3WithImageBg
  meta.maskColor = color
  meta.logoColor = getForegroundColor(color)
  meta.backgroundColor = undefined
}

export async function validateTagline (tagline: string, gift: GiftTemplate) {
  const data = { tagline, placeName: gift.places?.map(place => place.name).join(' ') }
  return customizer.validateTagline<{ status: string }>({ data })
}
